import React, { useEffect, useMemo, useRef, useState } from "react";
import { TimeSignature } from "../../../model/core";
import { CaretController } from "../../../utils/ScrollController";
import { tickToTimeSec } from "../../../utils/playbackTime";
import { RollRedrawObserver } from "../../../utils/RollRedrawObserver";
import { ComputedContextFrame } from "../../../model/context";


type PlaybackCaretIndicatorDialogProps = {
    ticksLength: number;
    getCurrentTimeSec?: () => number;
    currentTimeSec?: number;
    isPlaying?: boolean;
    className?: string;
    style?: React.CSSProperties;
    caretController?: CaretController;
    redrawObserver?: RollRedrawObserver;
    ppq: number;
    contextFrame: ComputedContextFrame;
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function formatTime(secondsInput: number): string {
    const seconds = Math.max(0, secondsInput || 0);
    const totalMillis = Math.round(seconds * 1000);
    const ms = totalMillis % 1000;
    const totalSeconds = Math.floor(totalMillis / 1000);
    const s = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const m = totalMinutes % 60;
    const h = Math.floor(totalMinutes / 60);

    const base =
        h > 0
            ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
            : `${m}:${s.toString().padStart(2, "0")}`;
    return `${base}.${ms.toString().padStart(3, "0")}`;
}

type BeatIndex = { bar: number; beat: number };

function secondsToBarBeat(
    secondsInput: number,
    bpm: number,
    ts: TimeSignature
): BeatIndex {
    const seconds = Math.max(0, secondsInput || 0);
    const qnPerBar = ts.numerator * (4 / ts.denominator);
    const totalQuarterBeats = (seconds * bpm) / 60;

    const epsilon = 1e-7;
    let barIdx = Math.floor((totalQuarterBeats + epsilon) / qnPerBar);
    let remainderQN = totalQuarterBeats - barIdx * qnPerBar;

    const unitBeatQN = 4 / ts.denominator;

    let beatIdx = Math.floor((remainderQN + epsilon) / unitBeatQN);
    if (beatIdx >= ts.numerator) {
        beatIdx = 0;
        barIdx += 1;
    }

    return { bar: barIdx + 1, beat: beatIdx + 1 };
}

function ticksToBarBeat(
    ticks: number,
    ppq: number,
    timeSignature: TimeSignature
) {
    const qnPerBar = timeSignature.numerator * (4 / timeSignature.denominator);
    const totalQuarterBeats = ticks / ppq;


    let barIdx = 0 | totalQuarterBeats / qnPerBar;
    let remainderQN = totalQuarterBeats - barIdx * qnPerBar;
    // console.log('ticksToBarBeat', { ticks, ppq, timeSignature, totalQuarterBeats, barIdx, remainderQN });
    return { bar: barIdx + 1, beat: 0 | remainderQN + 1 };
}


function formatBarBeat(bb: { bar: number; beat: number }) {
    return `${bb.bar}:${bb.beat}`;
}

// "Pro audio digital display" styling
const DIGIT_FONT =
    "'DSEG7', 'Digital-7 Mono', 'Share Tech Mono', 'Red Hat Mono', 'Roboto Mono', 'SFMono-Regular', Menlo, Consolas, monospace";

const panelStyleBase: React.CSSProperties = {
    padding: 12,
    borderRadius: 10,
    border: "1px solid #1a1a1a",
    background:
        "linear-gradient(180deg, #0a0a0c 0%, #0c0d10 50%, #0a0a0c 100%)",
    color: "#d6d6d6",
    width: 360,
    boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.03), 0 8px 20px rgba(0,0,0,0.45)",
};

const headerRow: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
};

const rowStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    // gap: 12,
    // padding: "6px 0",
        // outline: '1px solid red'
};

const labelBlock: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 110,
};

const labelStyle: React.CSSProperties = {
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: "#9aa0a6",
};

const valueStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    flexGrow: 1,
    gap: 10,
    fontVariantNumeric: "tabular-nums",
    alignItems: "center",
        // outline: '1px solid yellow'
};

const divider: React.CSSProperties = {
    height: 1,
    background: "linear-gradient(90deg, transparent, #1d1f23 20%, #1d1f23 80%, transparent)",
    margin: "6px 0",
};

const footerStyle: React.CSSProperties = {
    display: "flex",
    gap: 12,
    fontSize: 11,
    color: "#a8b0b7",
    opacity: 0.95,
};

const led = (on: boolean, colorOn = "#3dfbd0", colorOff = "#2a2f35"): React.CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background: on
        ? `radial-gradient(35% 35% at 35% 35%, ${colorOn} 0%, ${colorOn} 60%, rgba(0,0,0,0) 100%)`
        : `radial-gradient(35% 35% at 35% 35%, ${colorOff} 0%, ${colorOff} 60%, rgba(0,0,0,0) 100%)`,
    boxShadow: on
        ? `0 0 6px ${colorOn}88, 0 0 16px ${colorOn}55`
        : "inset 0 0 2px rgba(255,255,255,0.06)",
    border: "1px solid #0f1316",
});

const digitalBoxBase: React.CSSProperties = {
    padding: "2.5px 4px",
    borderRadius: 2,
    border: "1px solid #0f1518",
    background:
        "linear-gradient(180deg, rgba(5,9,12,0.95) 0%, rgba(7,12,15,0.9) 100%)",
    boxShadow:
        "inset 0 1px 6px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.02)",
    minWidth: 130,
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
    flexGrow: 1,
    // outline: '1px solid green'
};

const digitalText = (
    color: "cyan" | "amber" | "muted" = "cyan"
): React.CSSProperties => {
    const palette =
        color === "cyan"
            ? {
                  main: "#7efce7",
                  glow1: "rgba(126,252,231,0.75)",
                  glow2: "rgba(46,255,217,0.45)",
              }
            : color === "amber"
            ? {
                  main: "#ffd37a",
                  glow1: "rgba(255,211,122,0.75)",
                  glow2: "rgba(255,182,72,0.45)",
              }
            : {
                  main: "#aeb6bd",
                  glow1: "rgba(174,182,189,0.35)",
                  glow2: "rgba(174,182,189,0.18)",
              };

    return {
        fontFamily: DIGIT_FONT,
        fontWeight: 600,
        fontSize: 10,
        letterSpacing: "0.02em",
        color: palette.main,
        textShadow: `0 0 2px ${palette.glow1}, 0 0 2px ${palette.glow2}`,
        lineHeight: 1,
        userSelect: "none",
    };
};

const capSmall: React.CSSProperties = {
    fontSize: 10,
    color: "#7a8590",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginTop: 2,
};

const smallBadge: React.CSSProperties = {
    padding: "2px 6px",
    borderRadius: 4,
    border: "1px solid #151b20",
    background: "linear-gradient(180deg, #0b1014, #0a0e12)",
    color: "#aab2b8",
    fontSize: 11,
};

export function PlaybackCaretIndicatorDialog({
    ticksLength,
    getCurrentTimeSec,
    currentTimeSec,
    isPlaying = true,
    className,
    style,
    caretController,
    redrawObserver,
    ppq,
    contextFrame,
}: PlaybackCaretIndicatorDialogProps) {
    const [displayTime, setDisplayTime] = useState<number>(currentTimeSec ?? 0);
    const [endSec, setEndSec] = useState<number>(tickToTimeSec(ticksLength, ppq, contextFrame.metronome.bpm));
    const [timeSignature, setTimeSignature] = useState<TimeSignature>(contextFrame.timeSignature);
    const [ticksNow, setTicksNow] = useState<number>(0);
    const rafRef = useRef<number | null>(null);
    const [_, forceUpdate] = React.useReducer((x) => x + 1, 0);

    useEffect(() => {
        if (!getCurrentTimeSec && typeof currentTimeSec === "number") {
            setDisplayTime(currentTimeSec);
        }
    }, [currentTimeSec, getCurrentTimeSec]);

    useEffect(() => {
        if (!getCurrentTimeSec) return;
        const tick = () => {
            try {
                const t = getCurrentTimeSec();
                setDisplayTime(t);
            } finally {
                rafRef.current = window.requestAnimationFrame(tick);
            }
        };
        if (isPlaying) {
            rafRef.current = window.requestAnimationFrame(tick);
        } else {
            const t = getCurrentTimeSec();
            setDisplayTime(t);
        }
        return () => {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [getCurrentTimeSec, isPlaying, ticksNow]);

    useEffect(() => {
        if (!caretController?.scrollController) return;
        const listenerId = caretController?.register((o) => setTicksNow(o.ticks));
        return () => {
            caretController?.unregister(listenerId);
        }
    }, []);

    const clampedNow = useMemo(
        () => tickToTimeSec(ticksNow, ppq, contextFrame.metronome.bpm),
        [ticksNow, contextFrame.metronome.bpm]
    );

    const nowBB = useMemo(
        () => ticksToBarBeat(ticksNow, ppq, timeSignature),
        [ticksNow, ppq, timeSignature]
    );

    const endBB = useMemo(
        () => secondsToBarBeat(endSec, contextFrame.metronome.bpm, timeSignature),
        [endSec, contextFrame.metronome.bpm, timeSignature]
    );

    useEffect(() => {
        redrawObserver?.addObserver(() => {
            setTimeSignature(contextFrame.timeSignature);
            setEndSec(tickToTimeSec(ticksLength, ppq, contextFrame.metronome.bpm));
        });
        return () => {
            redrawObserver?.dropObserver(forceUpdate);
        };
    }, [redrawObserver]);

    return (
        <div style={rowStyle}>
            <div style={valueStyle}>
                <span style={{ ...digitalBoxBase }}>
                    <div style={{ marginRight: 10, textAlign: "right", minWidth: 120 }}>
                        <span style={digitalText("cyan")} title="Time">
                            {formatTime(clampedNow)}
                        </span>
                        /
                        <span style={digitalText("muted")} title="Time">
                            {formatTime(endSec)}
                        </span>
                    </div>
                    <div style={{ textAlign: "right", minWidth: 120 }}>
                        <span style={digitalText("amber")} title="Bar:Beat">
                            {formatBarBeat(nowBB)}
                        </span>
                        /
                        <span style={digitalText("muted")} title="Bar:Beat at End">
                            {formatBarBeat(endBB)}
                        </span>
                    </div>
                </span>
            </div>
        </div>
    );
};

export default PlaybackCaretIndicatorDialog;